<script lang="ts">
  import { onMount } from 'svelte';
  import {
    fetchCurrentUser,
    login,
    logout,
    rememberVerifiedUser,
    setupAdmin,
    type ClientUser
  } from './lib/auth/session';
  import { applyWaitingUpdateWhenSafe, subscribeUpdateWaiting } from './lib/offline/pwa-update';
  import TodayView from './components/TodayView.svelte';
  import ProductionView from './components/ProductionView.svelte';
  import { subscribePendingMutationCount, syncMutationQueue } from './lib/offline/queue';

  type Section = 'today' | 'production' | 'media' | 'doc' | 'more';

  let booting = true;
  let setupRequired = false;
  let user: ClientUser | null = null;
  let online = navigator.onLine;
  let pendingMutations = 0;
  let updateWaiting = false;
  let section: Section = 'today';
  let authError = '';
  let submitting = false;

  let email = '';
  let password = '';
  let displayName = '';
  let bootstrapToken = '';

  const nav: Array<{ id: Section; label: string }> = [
    { id: 'today', label: 'Oggi' },
    { id: 'production', label: 'Produzione' },
    { id: 'media', label: 'Media' },
    { id: 'doc', label: 'Doc' },
    { id: 'more', label: 'Altro' }
  ];

  onMount(() => {
    const offCount = subscribePendingMutationCount((value) => {
      pendingMutations = value;
      if (value === 0) void applyWaitingUpdateWhenSafe();
    });
    const offUpdate = subscribeUpdateWaiting((value) => (updateWaiting = value));

    const handleOnline = () => {
      online = true;
      void syncMutationQueue();
    };
    const handleOffline = () => (online = false);
    const handleAuthRequired = () => {
      user = null;
      setupRequired = false;
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('binder:auth-required', handleAuthRequired);

    void initialize();

    return () => {
      offCount();
      offUpdate();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('binder:auth-required', handleAuthRequired);
    };
  });

  async function initialize() {
    try {
      user = await fetchCurrentUser();
      if (user) {
        await rememberVerifiedUser(user);
        return;
      }

      if (navigator.onLine) {
        const response = await fetch('/api/setup/status');
        if (response.ok) {
          const data = (await response.json()) as { required: boolean };
          setupRequired = data.required;
        }
      }
    } finally {
      booting = false;
    }
  }

  async function submitAuth() {
    authError = '';
    submitting = true;
    try {
      const result = setupRequired
        ? await setupAdmin({ displayName, email, password, bootstrapToken })
        : await login(email, password);
      user = result.user;
      await rememberVerifiedUser(result.user);
      password = '';
      bootstrapToken = '';
    } catch {
      authError = setupRequired
        ? 'Non riesco a completare il setup. Verifica token e dati inseriti.'
        : 'Accesso non riuscito. Verifica email e password.';
    } finally {
      submitting = false;
    }
  }

  async function signOut() {
    await logout();
    user = null;
    setupRequired = false;
    if (navigator.onLine) {
      const response = await fetch('/api/setup/status');
      if (response.ok) setupRequired = ((await response.json()) as { required: boolean }).required;
    }
  }

  function selectSection(next: Section) {
    section = next;
  }

  function handleAuthSubmit(event: SubmitEvent) {
    event.preventDefault();
    void submitAuth();
  }
</script>

{#if booting}
  <main class="boot-screen" aria-live="polite">
    <div class="brand-mark">ARTIDE</div>
    <p>Production Binder</p>
  </main>
{:else if !user}
  <main class="auth-shell">
    <section class="auth-panel" aria-labelledby="auth-title">
      <div class="auth-brand">
        <span class="eyebrow">ARTIDE STUDIO</span>
        <h1 id="auth-title">Production Binder</h1>
        <p>{setupRequired ? 'Configurazione iniziale' : 'Accesso operativo'}</p>
      </div>

      {#if !online}
        <div class="state-message state-message--warning">
          <strong>Offline</strong>
          <span>Per accedere su questo dispositivo serve una sessione già verificata.</span>
        </div>
      {:else}
        <form class="auth-form" onsubmit={handleAuthSubmit}>
          {#if setupRequired}
            <label>
              <span>Nome</span>
              <input bind:value={displayName} autocomplete="name" required minlength="2" />
            </label>
          {/if}

          <label>
            <span>Email</span>
            <input bind:value={email} type="email" autocomplete="email" required />
          </label>

          <label>
            <span>Password</span>
            <input
              bind:value={password}
              type="password"
              autocomplete={setupRequired ? 'new-password' : 'current-password'}
              required
              minlength="10"
            />
          </label>

          {#if setupRequired}
            <label>
              <span>Bootstrap token</span>
              <input bind:value={bootstrapToken} type="password" autocomplete="off" required />
            </label>
          {/if}

          {#if authError}
            <p class="form-error" role="alert">{authError}</p>
          {/if}

          <button class="primary-button" type="submit" disabled={submitting}>
            {submitting ? 'Attendi…' : setupRequired ? 'Crea amministratore' : 'Accedi'}
          </button>
        </form>
      {/if}
    </section>
  </main>
{:else}
  <div class="app-shell">
    <aside class="desktop-rail" aria-label="Navigazione principale">
      <div class="rail-brand">
        <span>ARTIDE</span>
        <small>PRODUCTION BINDER</small>
      </div>

      <nav>
        {#each nav as item (item.id)}
          <button
            class:active={section === item.id}
            aria-current={section === item.id ? 'page' : undefined}
            onclick={() => selectSection(item.id)}
          >
            {item.label}
          </button>
        {/each}
      </nav>

      <div class="rail-footer">
        <span>{user.displayName}</span>
        <button onclick={signOut}>Esci</button>
      </div>
    </aside>

    <div class="app-main">
      <header class="topbar">
        <div>
          <span class="eyebrow">ARTIDE STUDIO</span>
          <strong>Production Binder</strong>
        </div>
        <div class="topbar-state" aria-live="polite">
          {#if !online}
            <span class="status status--warning">OFFLINE</span>
          {:else if pendingMutations > 0}
            <span class="status">SYNC · {pendingMutations}</span>
          {:else}
            <span class="status status--ok">SINCRONIZZATO</span>
          {/if}
        </div>
      </header>

      {#if !online || pendingMutations > 0 || updateWaiting}
        <div class="system-strip" aria-live="polite">
          {#if !online}
            <span><strong>Offline.</strong> Le modifiche supportate restano sul dispositivo.</span>
          {:else if pendingMutations > 0}
            <span><strong>{pendingMutations}</strong> {pendingMutations === 1 ? 'modifica da sincronizzare' : 'modifiche da sincronizzare'}.</span>
          {/if}
          {#if updateWaiting}
            <span>
              Aggiornamento disponibile{pendingMutations > 0 ? ': verrà applicato dopo il sync.' : '.'}
            </span>
          {/if}
        </div>
      {/if}

      <main class="workspace">
        {#if section === 'today'}
          <TodayView onOpenProduction={() => selectSection('production')} />
        {:else if section === 'production'}
          <ProductionView />
        {:else}
          <section class="not-yet" aria-labelledby="not-yet-title">
            <span class="eyebrow">OPERATIONAL CUT</span>
            <h1 id="not-yet-title">{nav.find((item) => item.id === section)?.label}</h1>
            <p>Questa sezione non viene simulata nello Slice 1. Verrà attivata nel relativo vertical slice.</p>
          </section>
        {/if}
      </main>

      <nav class="mobile-nav" aria-label="Navigazione principale">
        {#each nav as item (item.id)}
          <button
            class:active={section === item.id}
            aria-current={section === item.id ? 'page' : undefined}
            onclick={() => selectSection(item.id)}
          >
            {item.label}
          </button>
        {/each}
      </nav>
    </div>
  </div>
{/if}

<style>
  .boot-screen,
  .auth-shell {
    min-height: 100dvh;
    display: grid;
    place-items: center;
    padding: var(--space-6);
  }

  .boot-screen {
    background: var(--color-navy);
    color: var(--text-inverse);
    align-content: center;
    gap: var(--space-2);
  }

  .boot-screen p {
    margin: 0;
    color: #cdd3e2;
  }

  .brand-mark {
    font-size: var(--type-xl);
    font-weight: 700;
    letter-spacing: 0.12em;
  }

  .auth-shell {
    background: var(--background);
  }

  .auth-panel {
    width: min(100%, 430px);
    padding: clamp(24px, 7vw, 40px);
    background: var(--surface);
    border: 1px solid var(--border);
    border-top: 4px solid var(--color-navy);
    border-radius: var(--radius-lg);
  }

  .auth-brand {
    margin-bottom: var(--space-8);
  }

  .auth-brand h1,
  .page-heading h1,
  .not-yet h1 {
    margin: var(--space-2) 0 var(--space-2);
    font-size: var(--type-2xl);
    line-height: 1.05;
    letter-spacing: -0.035em;
  }

  .auth-brand p,
  .page-heading p,
  .not-yet p {
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.55;
  }

  .eyebrow {
    display: block;
    color: var(--color-blue);
    font-size: var(--type-xs);
    font-weight: 700;
    letter-spacing: 0.1em;
  }

  .auth-form {
    display: grid;
    gap: var(--space-4);
  }

  .auth-form label {
    display: grid;
    gap: var(--space-2);
  }

  .auth-form label > span {
    font-size: var(--type-sm);
    font-weight: 650;
  }

  input {
    width: 100%;
    min-height: 48px;
    padding: 0 var(--space-3);
    color: var(--text-primary);
    background: var(--surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
  }

  input:focus {
    border-color: var(--accent);
  }

  .primary-button {
    min-height: 48px;
    margin-top: var(--space-2);
    padding: 0 var(--space-4);
    border: 1px solid var(--color-navy);
    border-radius: var(--radius-sm);
    background: var(--color-navy);
    color: var(--text-inverse);
    font-weight: 700;
  }

  .primary-button:disabled {
    opacity: 0.55;
  }

  .form-error {
    margin: 0;
    color: var(--danger);
    font-size: var(--type-sm);
    line-height: 1.45;
  }

  .state-message {
    display: grid;
    gap: var(--space-1);
    padding: var(--space-4);
    border: 1px solid var(--border);
    border-left: 3px solid var(--warning);
    border-radius: var(--radius-sm);
    background: #fffaf0;
    line-height: 1.45;
  }

  .state-message span {
    color: var(--text-secondary);
  }

  .app-shell {
    min-height: 100dvh;
  }

  .desktop-rail {
    display: none;
  }

  .app-main {
    min-height: 100dvh;
    padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom));
  }

  .topbar {
    min-height: 68px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    background: rgba(246, 247, 249, 0.96);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 20;
    backdrop-filter: blur(12px);
  }

  .topbar strong {
    display: block;
    margin-top: 2px;
    font-size: var(--type-sm);
  }

  .status {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: 999px;
    color: var(--text-secondary);
    background: var(--surface);
    font-size: 0.68rem;
    font-weight: 750;
    letter-spacing: 0.055em;
  }

  .status--ok {
    color: var(--success);
    border-color: color-mix(in srgb, var(--success) 32%, var(--border));
  }

  .status--warning {
    color: var(--warning);
    border-color: color-mix(in srgb, var(--warning) 32%, var(--border));
  }

  .system-strip {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-5);
    padding: 10px var(--space-4);
    background: var(--color-surface-cold);
    border-bottom: 1px solid var(--border);
    color: var(--text-secondary);
    font-size: var(--type-xs);
    line-height: 1.35;
  }

  .system-strip strong {
    color: var(--text-primary);
  }

  .workspace {
    width: min(100%, var(--content-max));
    margin: 0 auto;
    padding: var(--space-8) var(--space-4) var(--space-16);
  }

  .page-heading {
    max-width: 670px;
    margin-bottom: var(--space-10);
  }

  .foundation-grid {
    display: grid;
    border-top: 1px solid var(--border-strong);
  }

  .foundation-grid article {
    min-height: 168px;
    padding: var(--space-5) 0;
    border-bottom: 1px solid var(--border);
  }

  .foundation-grid h2 {
    margin: var(--space-5) 0 var(--space-2);
    font-size: var(--type-lg);
    letter-spacing: -0.02em;
  }

  .foundation-grid p {
    max-width: 34rem;
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.55;
  }

  .section-index {
    color: var(--color-blue);
    font-size: var(--type-xs);
    font-variant-numeric: tabular-nums;
    font-weight: 750;
  }

  .not-yet {
    max-width: 620px;
    padding-top: var(--space-10);
  }

  .mobile-nav {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    min-height: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom));
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    padding: 0 var(--space-1) env(safe-area-inset-bottom);
    background: rgba(255, 255, 255, 0.98);
    border-top: 1px solid var(--border-strong);
  }

  .mobile-nav button {
    position: relative;
    min-width: 0;
    min-height: var(--bottom-nav-height);
    padding: 0 2px;
    border: 0;
    background: transparent;
    color: var(--text-secondary);
    font-size: clamp(0.64rem, 2.7vw, 0.74rem);
    font-weight: 650;
  }

  .mobile-nav button.active {
    color: var(--color-navy);
    font-weight: 800;
  }

  .mobile-nav button.active::before {
    content: '';
    position: absolute;
    top: 0;
    left: 22%;
    right: 22%;
    height: 3px;
    background: var(--accent);
  }

  @media (min-width: 768px) {
    .workspace {
      padding-inline: var(--space-8);
    }

    .foundation-grid {
      grid-template-columns: 1fr 1fr;
    }

    .foundation-grid article:nth-child(odd) {
      padding-right: var(--space-8);
      border-right: 1px solid var(--border);
    }

    .foundation-grid article:nth-child(even) {
      padding-left: var(--space-8);
    }
  }

  @media (min-width: 1000px) {
    .app-shell {
      display: grid;
      grid-template-columns: var(--rail-width) minmax(0, 1fr);
    }

    .desktop-rail {
      position: sticky;
      top: 0;
      height: 100dvh;
      display: flex;
      flex-direction: column;
      padding: var(--space-6) var(--space-4) var(--space-4);
      background: var(--surface);
      border-right: 1px solid var(--border);
    }

    .rail-brand {
      display: grid;
      gap: 2px;
      padding: 0 var(--space-2) var(--space-8);
    }

    .rail-brand span {
      color: var(--color-navy);
      font-weight: 850;
      letter-spacing: 0.08em;
    }

    .rail-brand small {
      color: var(--text-secondary);
      font-size: 0.62rem;
      letter-spacing: 0.08em;
    }

    .desktop-rail nav {
      display: grid;
      gap: 2px;
    }

    .desktop-rail nav button {
      position: relative;
      min-height: 42px;
      padding: 0 var(--space-3);
      text-align: left;
      border: 0;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--text-secondary);
      font-size: var(--type-sm);
    }

    .desktop-rail nav button.active {
      background: var(--color-surface-soft);
      color: var(--color-navy);
      font-weight: 750;
    }

    .desktop-rail nav button.active::before {
      content: '';
      position: absolute;
      top: 9px;
      bottom: 9px;
      left: 0;
      width: 3px;
      background: var(--accent);
    }

    .rail-footer {
      margin-top: auto;
      display: grid;
      gap: var(--space-2);
      padding: var(--space-4) var(--space-2) 0;
      border-top: 1px solid var(--border);
      font-size: var(--type-sm);
    }

    .rail-footer span {
      color: var(--text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rail-footer button {
      width: fit-content;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--color-navy);
      font-weight: 700;
    }

    .app-main {
      min-width: 0;
      padding-bottom: 0;
    }

    .topbar {
      padding-inline: var(--space-8);
    }

    .system-strip {
      padding-inline: var(--space-8);
    }

    .mobile-nav {
      display: none;
    }
  }
</style>
